import { useState, useEffect, useRef } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import * as fontkit from 'fontkit';

async function loadFont(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Font fetch failed: ${response.status} ${response.statusText}`);
    }
    const fontBytes = await response.arrayBuffer();
    return fontBytes;
  } catch (error) {
    console.error('Font loading error:', error);
    throw error;
  }
}


function wrapText(text, font, fontSize, maxWidth) {
  const words = (text || '').split(' ');
  const lines = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = font.widthOfTextAtSize(testLine, fontSize);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is too long, force it on its own line
        lines.push(word);
        currentLine = '';
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function wrapTextForimg(text, ctx, fontSize, maxWidth) {
  const words = (text || '').split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = ctx.measureText(testLine).width;
    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
}

async function preloadCertificateFont(fontUrl, fontName = "CertificateFont") {
  const response = await fetch(fontUrl);
  if (!response.ok) throw new Error(`Font fetch failed: ${fontUrl}`);
  const fontBytes = await response.arrayBuffer();

  try {
    // Register font for Canvas
    const fontFace = new FontFace(fontName, fontBytes);
    await fontFace.load();
    document.fonts.add(fontFace);
    await document.fonts.ready;
  } catch (err) {
    console.error(`FontFace load failed for ${fontName}:`, err);
  }

  return fontBytes;
}

export function useGenerateCertificate({
  userName,
  courseName,
  isMumbai,
  trigger,
  programCategoryId,
  programId,
  certificateUid,
  createdAt,
}) {
  const [generating, setGenerating] = useState(false);
  const [certificatePdfUrl, setCertificatePdfUrl] = useState(null);
  const [certificateImageUrl, setCertificateImageUrl] = useState(null);
  const [downloadDate, setDownloadDate] = useState(null);
  const [fontLoaded, setFontLoaded] = useState(false);
  const [fontBytes, setFontBytes] = useState(null);
  const [newFontBytes, setNewFontBytes] = useState(null);
  const uidRef = useRef(null);

  const isEcce = programCategoryId == 12 && programId == 83;
  const fontUrl = "https://s3.us-east-1.amazonaws.com/hunarho.com/FiraSans-Medium.ttf";
  const newFontUrl = "https://s3.us-east-1.amazonaws.com/hunarho.com/GreatVibes-Regular.ttf";

  function getUID() {
    if (!uidRef.current) {
      uidRef.current = generateCertificateUID();
    }
    return uidRef.current;
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const [bytes, gvBytes] = await Promise.all([
          preloadCertificateFont(fontUrl, "CertificateFont"),
          preloadCertificateFont(newFontUrl, "GreatVibes"),
        ]);
        if (mounted) {
          setFontBytes(bytes);
          setNewFontBytes(gvBytes);
          setFontLoaded(true);
        }
      } catch (err) {
        console.error("Font preload failed", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [fontUrl, newFontUrl]);

  useEffect(() => {
    if (!trigger || !fontLoaded) return;
    (async () => {
      setGenerating(true);
      try {
        await generateCertificate();
        await generateCertificateImage();
        setDownloadDate(new Date());
      } catch (e) {
        console.error(e);
      } finally {
        setGenerating(false);
      }
    })();
  }, [trigger, fontLoaded]);

  function generateCertificateUID() {
    const studentId = sessionStorage.getItem('id');
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let randomPart = "";

    for (let i = 0; i < 6; i++) {
      const randIndex = Math.floor(Math.random() * chars.length);
      randomPart += chars[randIndex];
    }

    return `H${studentId}${randomPart}`;
  }

  async function generateCertificate() {
    try {
      const templateFile = isEcce
        ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/ECCED_Certificate.pdf'
        : (isMumbai ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/uomcertificate.pdf' : 'https://s3.us-east-1.amazonaws.com/hunarho.com/freecourses.pdf');
      const existingPdfBytes = await fetch(templateFile).then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch ${templateFile}`);
        return res.arrayBuffer();
      });

      if (isEcce) {
        const pdfDoc = await PDFDocument.create();
        pdfDoc.registerFontkit(fontkit);

        const [templatePage] = await PDFDocument.load(existingPdfBytes).then(d => d.getPages());
        const embeddedPage = await pdfDoc.embedPage(templatePage);

        const page = pdfDoc.addPage([595.28, 841.89]);
        const { width, height } = page.getSize();

        const scaleX = width / templatePage.getSize().width;
        const scaleY = height / templatePage.getSize().height;
        const scale = Math.min(scaleX, scaleY);

        const scaledHeight = templatePage.getSize().height * scale;
        const yOffset = (height - scaledHeight) / 2;

        page.drawPage(embeddedPage, {
          x: 0,
          y: yOffset,
          xScale: scale,
          yScale: scale,
        });

        const kunstlerFont = await pdfDoc.embedFont(fontBytes);
        const calibriFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const nameFontSize = 33;
        const nameWidth = kunstlerFont.widthOfTextAtSize(userName, nameFontSize);
        const nameX = (width - nameWidth) / 2;
        const nameY = height * 0.577;
        page.drawText(userName, {
          x: nameX,
          y: nameY,
          size: nameFontSize,
          font: kunstlerFont,
          color: rgb(58 / 255, 58 / 255, 56 / 255),
        });

        const issueDate = new Date().toLocaleDateString('en-GB');
        page.drawText(issueDate, {
          x: width * 0.54,
          y: height * 0.352,
          size: 12,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });

        const uid = certificateUid || getUID();
        page.drawText(uid, {
          x: width * 0.53,
          y: height * 0.307,
          size: 12,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });

        const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
        setCertificatePdfUrl(pdfDataUri);
        return;
      }

      // Mumbai & Free Courses (Landscape)
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      pdfDoc.registerFontkit(fontkit);

      const page = pdfDoc.getPages()[0];
      const { width, height } = page.getSize();

      const studentFont = await pdfDoc.embedFont(newFontBytes || fontBytes);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Deep Navy Color (#012A60) matching template accents
      const navyColor = rgb(1 / 255, 42 / 255, 96 / 255);

      // 1. Student Name (Above Middle Line with GreatVibes font - matched to modal)
      let nameFontSize = 125;
      let nameWidth = studentFont.widthOfTextAtSize(userName, nameFontSize);
      const maxNameWidth = width * 0.75;
      if (nameWidth > maxNameWidth) {
        nameFontSize = (maxNameWidth / nameWidth) * nameFontSize;
        nameWidth = studentFont.widthOfTextAtSize(userName, nameFontSize);
      }
      const nameX = (width - nameWidth) / 2;
      const nameY = height * 0.495;

      page.drawText(userName, {
        x: nameX,
        y: nameY,
        size: nameFontSize,
        font: studentFont,
        color: navyColor,
      });

      // 2. Course Name (Under "Certificate Course Line" - matched to modal)
      const courseFontSize = 44;
      const maxCourseWidth = width * 0.85;
      const courseLines = wrapText(courseName, boldFont, courseFontSize, maxCourseWidth);
      const lineHeight = courseFontSize * 1.3;
      const courseStartY = height * 0.316 + ((courseLines.length - 1) * lineHeight) / 2;

      courseLines.forEach((line, i) => {
        const lineWidth = boldFont.widthOfTextAtSize(line, courseFontSize);
        const lineX = (width - lineWidth) / 2;
        const lineY = courseStartY - i * lineHeight;
        page.drawText(line, {
          x: lineX,
          y: lineY,
          size: courseFontSize,
          font: boldFont,
          color: navyColor,
        });
      });

      // 3. Issue Date (Centered above the Date of Issue line - matched to modal)
      const issueDate = new Date().toLocaleDateString('en-GB');
      const dateFontSize = 28;
      const dateWidth = boldFont.widthOfTextAtSize(issueDate, dateFontSize);
      const lineCenterX = width * 0.303;
      const dateX = lineCenterX - (dateWidth / 2);
      const dateY = height * 0.215; // Above the gold line in PDF coordinates
      page.drawText(issueDate, {
        x: dateX,
        y: dateY,
        size: dateFontSize,
        font: boldFont,
        color: navyColor,
      });

      // 4. UID (Matched to modal, shifted 5% left)
      const uid = certificateUid || getUID();
      const uidText = `Certificate UID: ${uid}`;
      const uidFontSize = 18;
      const uidWidth = boldFont.widthOfTextAtSize(uidText, uidFontSize);
      page.drawText(uidText, {
        x: (width - uidWidth) / 2 - width * 0.02,
        y: height * 0.033,
        size: uidFontSize,
        font: boldFont,
        color: navyColor,
      });

      // Save PDF
      const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });
      setCertificatePdfUrl(pdfDataUri);

    } catch (err) {
      console.error('Certificate generation error:', err);
    }
  }

  async function generateCertificateImage() {
    try {
      const offscreenCanvas = document.createElement('canvas');
      const ctx = offscreenCanvas.getContext('2d');

      const certificateImage = new Image();
      certificateImage.crossOrigin = "anonymous";
      certificateImage.src = isEcce
        ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/ECCED_Certificate.jpg'
        : (isMumbai ? 'https://s3.us-east-1.amazonaws.com/hunarho.com/uomcertificate.png' : 'https://s3.us-east-1.amazonaws.com/hunarho.com/freecourses.png');

      await new Promise((resolve, reject) => {
        certificateImage.onload = resolve;
        certificateImage.onerror = reject;
      });

      offscreenCanvas.width = certificateImage.width;
      offscreenCanvas.height = certificateImage.height;

      if (!ctx) {
        throw new Error('Error creating canvas context');
      }

      ctx.drawImage(certificateImage, 0, 0);

      const { width, height } = offscreenCanvas;

      if (isEcce) {
        // Student Name
        ctx.font = '140px "CertificateFont"';
        ctx.fillStyle = '#3a3a38';
        const nameWidth = ctx.measureText(userName).width;
        const nameX = (width - nameWidth) / 2;
        const nameY = height * 0.423;
        ctx.fillText(userName, nameX, nameY);

        // issueDate
        const issueDate = new Date().toLocaleDateString('en-GB');
        ctx.fillStyle = 'black';
        ctx.font = '48px Helvetica';
        const dateX = width * 0.54;
        const dateY = height * 0.648;
        ctx.fillText(issueDate, dateX, dateY);

        // UID
        const uid = certificateUid || getUID();
        ctx.font = '48px Helvetica';
        const uidX = width * 0.53;
        const uidY = height * 0.693;
        ctx.fillText(uid, uidX, uidY);
      } else {
        // Deep Navy Color (#012A60) matching template accents
        const navyColor = '#012A60';

        // 1. Student Name (Above Middle Line with GreatVibes font)
        let nameFontSize = 165;
        ctx.font = `${nameFontSize}px "GreatVibes", cursive`;
        let nameWidth = ctx.measureText(userName).width;
        const maxNameWidth = width * 0.75;
        if (nameWidth > maxNameWidth) {
          nameFontSize = Math.floor((maxNameWidth / nameWidth) * nameFontSize);
          ctx.font = `${nameFontSize}px "GreatVibes", cursive`;
          nameWidth = ctx.measureText(userName).width;
        }
        ctx.fillStyle = navyColor;
        const nameX = (width - nameWidth) / 2;
        const nameY = height * 0.512; // Baseline with clean space above middle line
        ctx.fillText(userName, nameX, nameY);

        // 2. Course Name (Under "Certificate Course Line")
        const courseFontSize = 52;
        ctx.font = `bold ${courseFontSize}px Helvetica, Arial, sans-serif`;
        ctx.fillStyle = navyColor;
        const maxCourseWidth = width * 0.85;
        const courseLines = wrapTextForimg(courseName, ctx, courseFontSize, maxCourseWidth);
        const courseLineHeight = courseFontSize * 1.3;
        const courseTotalHeight = courseLines.length * courseLineHeight;
        const courseStartY = height * 0.672 - (courseTotalHeight / 2) + courseFontSize;

        courseLines.forEach((line, i) => {
          const lineWidth = ctx.measureText(line).width;
          const lineX = (width - lineWidth) / 2;
          const lineY = courseStartY + i * courseLineHeight;
          ctx.fillText(line, lineX, lineY);
        });

        // 3. Issue Date (Centered above the Date of Issue line)
        const issueDate = new Date().toLocaleDateString('en-GB');
        ctx.font = 'bold 38px Helvetica, Arial, sans-serif';
        ctx.fillStyle = navyColor;
        const dateWidth = ctx.measureText(issueDate).width;
        const lineCenterX = width * 0.303;
        const dateX = lineCenterX - (dateWidth / 2);
        const dateY = height * 0.785; // Above the gold line (which is at height * 0.804)
        ctx.fillText(issueDate, dateX, dateY);

        // 4. UID (Shifted 5% left)
        const uid = certificateUid || getUID();
        const uidVal = `Certificate UID: ${uid}`;
        ctx.font = 'bold 24px Helvetica, Arial, sans-serif';
        ctx.fillStyle = navyColor;
        const uidWidth = ctx.measureText(uidVal).width;
        const uidX = (width - uidWidth) / 2 - width * 0.02;
        const uidY = height * 0.968;
        ctx.fillText(uidVal, uidX, uidY);
      }

      const dataUrl = offscreenCanvas.toDataURL();
      setCertificateImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating certificate image:', error);
    }
  }

  return {
    generating,
    certificatePdfUrl,
    certificateImageUrl,
    downloadDate,
    fontLoaded
  };
}
